import React from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';

/**
 * Styled confirmation dialog that replaces window.confirm().
 * Renders a Modal with a message, optional description, and Confirm/Cancel buttons.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {function} props.onConfirm - Callback when user clicks confirm
 * @param {function} props.onCancel - Callback when user clicks cancel or closes modal
 * @param {string} props.title - Modal header title
 * @param {string} props.message - Primary message shown in the modal body
 * @param {string} [props.description] - Optional secondary description text
 * @param {string} [props.confirmLabel='Confirm'] - Text for the confirm button
 * @param {string} [props.cancelLabel='Cancel'] - Text for the cancel button
 * @param {'danger'|'primary'} [props.variant='primary'] - Visual style of the confirm button
 * @returns {JSX.Element|null} The confirmation modal, or null when closed
 */
function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
}) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} size="small">
      <div className="confirm-modal__body">
        <p className="confirm-modal__message">
          {message}
        </p>
        {description && (
          <p className="confirm-modal__description">
            {description}
          </p>
        )}
        <div className="confirm-modal__actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

ConfirmModal.propTypes = {
  /** Whether the modal is visible */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when user clicks confirm */
  onConfirm: PropTypes.func.isRequired,
  /** Callback when user clicks cancel or closes modal */
  onCancel: PropTypes.func.isRequired,
  /** Modal header title */
  title: PropTypes.string.isRequired,
  /** Primary message shown in the modal body */
  message: PropTypes.string.isRequired,
  /** Optional secondary description text */
  description: PropTypes.string,
  /** Text for the confirm button */
  confirmLabel: PropTypes.string,
  /** Text for the cancel button */
  cancelLabel: PropTypes.string,
  /** Visual style of the confirm button */
  variant: PropTypes.oneOf(['danger', 'primary']),
};

export default ConfirmModal;
